import {
  Component,
  AfterViewInit,
  ElementRef,
  ViewChild,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  inject,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import * as L from 'leaflet';
import * as topojson from 'topojson-client';
import usData from '../nation-chart/us.json';
import { ThemeService, Theme } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-geographic-map',
  templateUrl: './geographic-map.component.html',
  styleUrls: ['./geographic-map.component.scss'],
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None
})
export class GeographicMapComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() primaryLineColor: string = '';
  @Input() stateData: any[] = [];
  @Input() valueKeys: any = {
    keyName: 'state',
    valueKey: 'count',
  };
  // When set, a feature is only interactive (tooltip shown, hover-highlight, click emits) if
  // ANY of these record fields is > 0 — independent of `valueKeys.valueKey` (which only drives
  // fill color). Leave unset to keep the original behavior (interactive iff the active metric > 0).
  @Input() tooltipEligibleKeys?: string[];
  @Output() regionClick = new EventEmitter<any>();

  @ViewChild('mapContainer', { static: true }) private chartContainer!: ElementRef;

  private map!: L.Map;
  private geoJsonLayer!: L.GeoJSON;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';
  private themeService = inject(ThemeService);

  // Esri Gray Canvas tiles (no API key required; CARTO's anonymous basemaps now demand one)
  private readonly tilesLight = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
  private readonly tilesDark = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}';
  private currentTileLayer!: L.TileLayer;
  private readonly metricAliases = [
    {
      label: 'Businesses',
      keys: ['businesses', 'business', 'businessCount', 'business_count', 'businessesCount']
    },
    {
      label: 'Contracts',
      keys: ['contracts', 'contract', 'contractCount', 'contract_count', 'contractsCount']
    },
    {
      label: 'Leads',
      keys: ['leads', 'lead', 'leadCount', 'lead_count', 'leadsCount']
    }
  ];

  ngAfterViewInit(): void {
    this.currentTheme = this.themeService.getTheme();
    this.initMap();

    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.updateTheme();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.geoJsonLayer && (changes['stateData'] || changes['valueKeys'] || changes['primaryLineColor'])) {
      // Only restyle/retooltip for the new data — do NOT touch the map's position/zoom here.
      // Switching metrics (e.g. Businesses <-> Contracts) must never move or rescale the map.
      this.refreshLayerStylesAndTooltips();
    }
  }

  // Fixed default view — the map always loads at, and the Home button always resets to,
  // this exact center/zoom regardless of which states have data. Never derived from data
  // bounds (that was the previous behavior and caused the view to shift per metric/dataset).
  private static readonly DEFAULT_CENTER: L.LatLngExpression = [37.8, -96];
  private static readonly DEFAULT_ZOOM = 4;

  private resetToDefaultView(): void {
    if (!this.map) return;
    this.map.setView(GeographicMapComponent.DEFAULT_CENTER, GeographicMapComponent.DEFAULT_ZOOM, { animate: false });
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
    if (this.map) {
      this.map.remove();
    }
  }

  private initMap(): void {
    this.map = L.map(this.chartContainer.nativeElement, {
      center: GeographicMapComponent.DEFAULT_CENTER,
      zoom: GeographicMapComponent.DEFAULT_ZOOM,
      minZoom: GeographicMapComponent.DEFAULT_ZOOM,
      maxZoom: GeographicMapComponent.DEFAULT_ZOOM,
      zoomControl: false, // We will move it
      // Lock the map to the fixed default view — no user-driven zoom or pan.
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      touchZoom: false,
      keyboard: false
    });

    this.map.on('dragstart zoomstart', () => {
      if (this.geoJsonLayer) {
        this.geoJsonLayer.eachLayer((l: any) => {
          if (typeof l.closeTooltip === 'function') {
            l.closeTooltip();
          }
        });
      }
    });

    const customZoom = new L.Control({ position: 'topleft' });
    customZoom.onAdd = (map: L.Map) => {
      const container = L.DomUtil.create('div', 'custom-leaflet-zoom-control');

      const createBtn = (html: string, title: string, onClick: () => void) => {
        const btn = L.DomUtil.create('button', 'custom-leaflet-btn', container);
        btn.innerHTML = html;
        btn.title = title;
        btn.setAttribute('type', 'button');
        L.DomEvent.on(btn, 'click', (e: Event) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          onClick();
        });
        L.DomEvent.on(btn, 'dblclick', L.DomEvent.stopPropagation);
        return btn;
      };

      createBtn('+', 'Zoom in', () => map.zoomIn());
      createBtn('&minus;', 'Zoom out', () => map.zoomOut());
      createBtn('<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>', 'Reset view', () => this.resetToDefaultView());

      return container;
    };
    customZoom.addTo(this.map);

    this.currentTileLayer = L.tileLayer(this.currentTheme === 'dark' ? this.tilesDark : this.tilesLight, {
      attribution: 'Tiles &copy; <a href="https://www.esri.com">Esri</a>',
      maxZoom: 20,
      maxNativeZoom: 16
    }).addTo(this.map);

    this.addChoropleth();
  }

  private updateTheme(): void {
    if (this.map && this.currentTileLayer) {
      this.currentTileLayer.setUrl(this.currentTheme === 'dark' ? this.tilesDark : this.tilesLight);
      if (this.geoJsonLayer) {
        this.refreshLayerStylesAndTooltips();
      }
    }
  }

  private refreshLayerStylesAndTooltips(): void {
    this.geoJsonLayer.eachLayer((layer: any) => {
      this.geoJsonLayer.resetStyle(layer);

      // Data (e.g. the selected date range) may have changed which features are interactive,
      // so tooltips are (re)bound or unbound here rather than assumed static.
      if (this.isInteractive(layer.feature)) {
        if (layer.getTooltip()) {
          layer.setTooltipContent(this.getTooltipContent(layer.feature));
        } else {
          layer.bindTooltip(this.getTooltipContent(layer.feature), {
            sticky: true,
            className: 'custom-leaflet-tooltip',
            animation: false
          } as any);
        }
      } else if (layer.getTooltip()) {
        layer.closeTooltip();
        layer.unbindTooltip();
      }
    });
  }

  private STATE_NAME_TO_ABBR: { [key: string]: string } = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
    'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
    'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
    'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
    'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH',
    'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC',
    'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA',
    'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD', 'Tennessee': 'TN',
    'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA',
    'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC'
  };

  private normalizeName(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private getRegionRecord(feature: any): any | undefined {
    const stateName = feature?.properties?.name;
    const countryCode = feature?.properties?._countryCode;

    const keyNames = [
      this.valueKeys?.keyName || 'state',
      'stateName',
      'state_name',
      'name',
      'region',
      'country'
    ];
    const normalizedStateName = this.normalizeName(stateName);
    const abbr = this.STATE_NAME_TO_ABBR[stateName];
    const normalizedAbbr = abbr ? this.normalizeName(abbr) : null;

    return this.stateData?.find((item) => {
      // If country code is known for the feature, it must match the item's country code (if the item provides one)
      if (countryCode && item.country_code) {
        const itemCc = String(item.country_code).trim().toUpperCase();
        const fCc = String(countryCode).trim().toUpperCase();
        if (itemCc !== fCc && itemCc !== 'USA' && fCc !== 'US') {
          return false;
        }
      }

      return keyNames.some((keyName) => {
        const itemVal = this.normalizeName(item?.[keyName]);
        return itemVal === normalizedStateName || (normalizedAbbr && itemVal === normalizedAbbr);
      });
    });
  }

  private getValueFromRecord(record: any, candidateKeys: string | string[]): number {
    if (!record) {
      return 0;
    }

    const keys = Array.isArray(candidateKeys) ? candidateKeys : [candidateKeys];
    const recordKeys = Object.keys(record);

    for (const key of keys) {
      if (key in record) {
        const value = Number(record[key]);
        if (!Number.isNaN(value)) {
          return value;
        }
      }

      const caseInsensitiveKey = recordKeys.find((recordKey) => this.normalizeName(recordKey) === this.normalizeName(key));
      if (caseInsensitiveKey) {
        const value = Number(record[caseInsensitiveKey]);
        if (!Number.isNaN(value)) {
          return value;
        }
      }
    }

    return 0;
  }

  private getActiveMetricKeys(): string[] {
    const valueKey = this.valueKeys?.valueKey || 'count';
    const configuredMetric = this.metricAliases.find((metric) =>
      metric.keys.some((key) => this.normalizeName(key) === this.normalizeName(valueKey))
    );

    return configuredMetric ? configuredMetric.keys : [valueKey];
  }

  private getActiveMetricLabel(): string {
    const valueKey = this.valueKeys?.valueKey || 'count';
    const configuredMetric = this.metricAliases.find((metric) =>
      metric.keys.some((key) => this.normalizeName(key) === this.normalizeName(valueKey))
    );

    return this.valueKeys?.label || configuredMetric?.label || this.toTitleCase(valueKey);
  }

  private getStateValue(feature: any): number {
    return this.getValueFromRecord(this.getRegionRecord(feature), this.getActiveMetricKeys());
  }

  // Whether a feature should show a tooltip / hover-highlight / emit clicks. When
  // `tooltipEligibleKeys` is set, this is independent of the active metric (`valueKeys.valueKey`) —
  // otherwise it falls back to the original "active metric > 0" behavior.
  private isInteractive(feature: any): boolean {
    if (this.tooltipEligibleKeys && this.tooltipEligibleKeys.length) {
      const record = this.getRegionRecord(feature);
      if (!record) return false;
      return this.tooltipEligibleKeys.some((key) => this.getValueFromRecord(record, [key]) > 0);
    }
    return this.getStateValue(feature) > 0;
  }

  private getColorOpacity(d: number, maxVal: number): number {
    if (!d || !maxVal) return 0;
    const t = Math.min(1, d / maxVal);
    // Base minimum opacity of 0.50 so it's very clearly visible
    return 0.5 + (t * 0.5);
  }

  private style = (feature: any): L.PathOptions => {
    const val = this.getStateValue(feature);
    const isDark = this.currentTheme === 'dark';

    // Find max value for dynamic scaling
    const activeKeys = this.getActiveMetricKeys();
    const maxVal = Math.max(...(this.stateData?.map(s => this.getValueFromRecord(s, activeKeys)) || [1]), 1);

    const baseColor = this.primaryLineColor || (isDark ? '#a78bfa' : '#a855f7');
    const emptyColor = isDark ? '#1e1e21' : '#f1f5f9';

    return {
      fillColor: val > 0 ? baseColor : emptyColor,
      weight: 1,
      opacity: 1,
      color: isDark ? '#2e2e33' : '#cbd5e1', // visible grey border instead of white
      fillOpacity: val > 0 ? this.getColorOpacity(val, maxVal) : (isDark ? 0.3 : 0.7)
    };
  };

  private getTooltipMetricRows(record: any): { label: string; value: number; isActive: boolean }[] {
    const activeLabel = this.getActiveMetricLabel();
    const activeKeys = this.getActiveMetricKeys().map((key) => this.normalizeName(key));
    const rows = this.metricAliases
      .filter((metric) => metric.keys.some((key) => this.getRecordKey(record, key)))
      .map((metric) => ({
        label: metric.label,
        value: this.getValueFromRecord(record, metric.keys),
        isActive: metric.keys.some((key) => activeKeys.includes(this.normalizeName(key)))
      }));

    if (rows.length) {
      return rows;
    }

    return [{
      label: activeLabel,
      value: this.getValueFromRecord(record, this.getActiveMetricKeys()),
      isActive: true
    }];
  }

  private getRecordKey(record: any, key: string): string | undefined {
    if (!record) {
      return undefined;
    }

    if (key in record) {
      return key;
    }

    return Object.keys(record).find((recordKey) => this.normalizeName(recordKey) === this.normalizeName(key));
  }

  private toTitleCase(value: string): string {
    return String(value)
      .replace(/[_-]+/g, ' ')
      .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  }

  private getTooltipContent(feature: any): string {
    const val = this.getStateValue(feature);
    const record = this.getRegionRecord(feature);
    const rows = this.getTooltipMetricRows(record);
    const industryLabel = this.valueKeys?.industryLabel || 'All Industries';
    const metricRowsHtml = rows.map((row) => `
        <div class="geo-tooltip-row ${row.isActive ? 'is-active' : ''}">
          <span>${row.label}</span>
          <strong>${row.value}</strong>
        </div>
      `).join('');

    let countryName = 'United States';
    if (feature.properties?._countryCode === 'GB') countryName = 'United Kingdom';
    if (feature.properties?._countryCode === 'AU') countryName = 'Australia';
    if (feature.properties?._countryCode === 'DE') countryName = 'Germany';
    if (feature.properties?._countryCode === 'CA') countryName = 'Canada';

    return `
      <div class="geo-tooltip">
        <strong class="geo-tooltip-title">${feature.properties.name}</strong>
        <span class="geo-tooltip-subtitle">${countryName} - ${industryLabel}</span>
        <div class="geo-tooltip-metrics">
          ${metricRowsHtml}
        </div>
        <em class="geo-tooltip-help">Click for onboarding trends & top accounts</em>
      </div>
    `;
  }

  private async addChoropleth(): Promise<void> {
    const urls: Record<string, string> = {
      'US': 'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json',
      'GB': 'https://raw.githubusercontent.com/martinjc/UK-GeoJSON/master/json/administrative/gb/lad.json',
      'AU': 'https://raw.githubusercontent.com/tonywr71/GeoJSON-Australia/master/states.geojson',
      'DE': 'https://raw.githubusercontent.com/isellsoap/deutschlandGeoJSON/main/2_bundeslaender/4_niedrig.geo.json',
      'CA': 'https://raw.githubusercontent.com/codeforgermany/click_that_hood/main/public/data/canada.geojson'
    };

    try {
      const activeCountries = Array.from(new Set(this.stateData?.map(s => String(s.country_code || s.country || 'US').toUpperCase()) || ['US']));

      const features: any[] = [];

      for (const cc of activeCountries) {
        const countryCode = cc === 'USA' ? 'US' : (cc === 'UK' ? 'GB' : cc);
        const url = urls[countryCode] || urls['US'];
        if (url) {
          try {
            const res = await fetch(url);
            if (res.ok) {
              const data = await res.json();
              if (data && data.features) {
                // Annotate features with country code to avoid collisions (e.g. WA state in US vs AU)
                data.features.forEach((f: any) => {
                  if (!f.properties) f.properties = {};
                  f.properties._countryCode = countryCode;

                  // Fix antimeridian crossing for Alaska
                  if (f.properties.name === 'Alaska' && f.geometry && f.geometry.coordinates) {
                    const fixAntimeridian = (coords: any[]) => {
                      coords.forEach((coord: any) => {
                        if (Array.isArray(coord[0])) {
                          fixAntimeridian(coord);
                        } else if (coord.length >= 2 && coord[0] > 0) {
                          coord[0] -= 360;
                        }
                      });
                    };
                    fixAntimeridian(f.geometry.coordinates);
                  }
                });
                features.push(...data.features);
              }
            }
          } catch (e) {
            console.error(`Failed to load geojson for ${countryCode}:`, e);
          }
        }
      }

      // If everything failed, fallback to local US Data (TopoJSON)
      if (features.length === 0) {
        const localFeatures = (topojson.feature(usData as any, (usData as any).objects.states) as any).features;
        localFeatures.forEach((f: any) => {
          if (!f.properties) f.properties = {};
          f.properties._countryCode = 'US';
        });
        features.push(...localFeatures);
      }

      this.geoJsonLayer = L.geoJSON({ type: 'FeatureCollection', features } as any, {
        style: this.style,
        onEachFeature: (feature: any, layer: any) => {
          if (this.isInteractive(feature)) {
            layer.bindTooltip(this.getTooltipContent(feature), {
              sticky: true,
              className: 'custom-leaflet-tooltip',
              animation: false
            } as any);
          }

          layer.on({
            click: (e: any) => {
              if (this.isInteractive(feature)) {
                this.regionClick.emit({
                  feature: feature,
                  record: this.getRegionRecord(feature),
                  stateValue: this.getStateValue(feature)
                });
              }
            },
            mouseover: (e: any) => {
              // Close any other open tooltips
              this.geoJsonLayer.eachLayer((l: any) => {
                if (l !== e.target && typeof l.closeTooltip === 'function') {
                  l.closeTooltip();
                }
              });

              if (this.isInteractive(feature)) {
                const l = e.target;
                l.setStyle({
                  weight: 2,
                  color: '#6366f1',
                  fillOpacity: 0.9
                });
                // Change cursor to pointer
                if (l.getElement) {
                  l.getElement().style.cursor = 'pointer';
                }
              } else {
                const l = e.target;
                if (l.getElement) {
                  l.getElement().style.cursor = 'default';
                }
              }
            },
            mousemove: (e: any) => {
              const tooltip = layer.getTooltip();
              if (tooltip) {
                const tooltipEl = tooltip.getElement();
                if (tooltipEl && this.map) {
                  const mapHeight = this.map.getSize().y;
                  const tooltipHeight = tooltipEl.offsetHeight;
                  const y = e.containerPoint.y;

                  let offsetY = 0;
                  const padding = 10;
                  const defaultTop = y - tooltipHeight / 2;
                  const defaultBottom = y + tooltipHeight / 2;

                  if (defaultTop < padding) {
                    offsetY = padding - defaultTop;
                  } else if (defaultBottom > mapHeight - padding) {
                    offsetY = (mapHeight - padding) - defaultBottom;
                  }

                  let currentY = 0;
                  const currentOffset = tooltip.options.offset as any;
                  if (currentOffset) {
                    currentY = currentOffset.y !== undefined ? currentOffset.y : (currentOffset[1] || 0);
                  }

                  if (currentY !== offsetY) {
                    tooltip.options.offset = L.point(0, offsetY);
                    if (typeof (tooltip as any)._updatePosition === 'function') {
                      (tooltip as any)._updatePosition();
                    } else if (typeof (tooltip as any).update === 'function') {
                      (tooltip as any).update();
                    }
                  }
                }
              }
            },
            mouseout: (e: any) => {
              if (this.isInteractive(feature)) {
                this.geoJsonLayer.resetStyle(e.target);
              }
            }
          });
        }
      }).addTo(this.map);
      this.resetToDefaultView();
    } catch (err) {
      console.error('Error adding choropleth:', err);
    }
  }
}
